import moment from 'moment'
import { AppointmentData, ClientData, CompanyLegalInfo, InvoiceDocumentType, InvoiceItem, InvoiceItemDetails, InvoiceItemType, InvoiceSettingsData } from '../../models'
import { formatEuros } from '../string'
import { getTotalPrice } from './appointment'
import { Translate, TK } from '../../store/translations/translationKeys'
import { resolvePlaceholders } from '../placeholders'

/**
 * Convert invoice document type to readable text
 */
export const getInvoiceTypeText = (type: InvoiceDocumentType, t: Translate<TK>): string => {
    switch(type) {
        case InvoiceDocumentType.Invoice: return t(TK.invoice)
        case InvoiceDocumentType.InvoiceReceipt: return t(TK.invoiceReceipt)
        case InvoiceDocumentType.SimplifiedInvoice: return t(TK.simplifiedInvoice)
        case InvoiceDocumentType.CreditNote: return t(TK.creditNote)
        case InvoiceDocumentType.Receipt: return t(TK.receipt)
        default: return InvoiceDocumentType[type]
    }
}

/**
 * Normalize tax string by trimming and returning undefined for empty strings
 */
export const normalizeTaxString = (value?: string | null): string | undefined => {
    if (typeof value !== 'string') {
        return undefined;
    }

    const trimmed = value.trim();
    return trimmed.length ? trimmed : undefined;
};

/**
 * Resolve tax details for an invoice item
 */
export const resolveTaxDetails = (
    details?: Pick<InvoiceItemDetails, 'taxId' | 'taxExemption' | 'taxExemptionLaw'>,
    companyLegalInfo?: CompanyLegalInfo,
) => {
    const hasCustomTax = details?.taxId != null;
    const taxId = hasCustomTax ? details?.taxId : companyLegalInfo?.taxId;

    const companyExemption = normalizeTaxString(companyLegalInfo?.taxExemption);
    const companyExemptionLaw = normalizeTaxString(companyLegalInfo?.taxExemptionLaw);
    const itemExemption = normalizeTaxString(details?.taxExemption);
    const itemExemptionLaw = normalizeTaxString(details?.taxExemptionLaw);

    const taxExemption = hasCustomTax ? (itemExemption ?? companyExemption) : companyExemption;
    const taxExemptionLaw = taxExemption ? (itemExemptionLaw ?? companyExemptionLaw) : undefined;

    return { taxId, taxExemption, taxExemptionLaw };
};

/**
 * Resolve the effective legal info for a given branch, falling back to the global companyLegalInfo.
 */
export const resolveEffectiveLegalInfo = (
    invoiceSettings: Pick<InvoiceSettingsData, 'companyLegalInfo' | 'branchesLegalInfo'> | undefined | null,
    branchId?: string | null,
): CompanyLegalInfo | undefined => {
    const company = invoiceSettings?.companyLegalInfo;
    const branch = branchId ? invoiceSettings?.branchesLegalInfo?.[branchId] : undefined;

    if (!branch) return company;

    return {
        address: branch.address ?? company?.address,
        postalCode: branch.postalCode ?? company?.postalCode,
        city: branch.city ?? company?.city,
        taxId: branch.taxId ?? company?.taxId,
        taxExemption: branch.taxExemption ?? company?.taxExemption,
        taxExemptionLaw: branch.taxExemptionLaw ?? company?.taxExemptionLaw,
    };
};

type AppointmentPlaceholderServices = Record<string, { name?: string; invoiceItemDetails?: { customTitle?: string | null } }>;

/**
 * Replace appointment placeholders in a template string.
 * Supported: {date}, {appointment-type}, {invoice-appointment-type}, {professional}, {price}
 */
export const applyAppointmentPlaceholders = (
    template: string,
    appointment?: AppointmentData,
    services?: AppointmentPlaceholderServices,
    client?: ClientData,
): string => {
    const invoiceAppointmentType = !appointment ? '' : (services?.[appointment.type]?.invoiceItemDetails?.customTitle
        || services?.[appointment.type]?.name
        || appointment.type);

    return resolvePlaceholders(template, {
        date: appointment ? moment(appointment.date).format('YYYY-MM-DD') : undefined,
        appointmentType: !appointment ? undefined : (services?.[appointment.type]?.name || appointment.type),
        invoiceAppointmentType,
        professionalName: appointment?.professionalName ?? 'Unknown',
        price: appointment ? formatEuros(getTotalPrice(appointment)) : undefined,
        clientName: client?.name,
        clientIdentificationNumber: client?.identificationNumber,
        clientTaxNumber: client?.taxNumber,
        clientHealthNumber: client?.healthNumber,
        clientSubsystemName: client?.subsystemName,
        clientSubsystemNumber: client?.subsystemNumber,
        clientBirthday: client?.birthday,
        clientPhoneNumber: client?.phoneNumbers?.[0],
        clientEmail: client?.email,
        clientFileNumber: client?.clientFileNumber?.toString(),
        clientProperties: client?.properties,
    });
};

/**
 * Map extras (appointment expenses) to invoice items
 */
export const mapExtrasToInvoiceItems = <T extends { id: string; name: string; value: number; quantity: number }>(
    extras: T[],
    extrasSettings: any[],
    companyLegalInfo?: CompanyLegalInfo,
    appointment?: AppointmentData,
    services?: AppointmentPlaceholderServices,
    client?: ClientData,
): InvoiceItem[] => {
    return extras.filter(e => e.value > 0).map((e): InvoiceItem => {
        const extra = extrasSettings.find(ex => ex?.id === e.id);
        const { taxId, taxExemption, taxExemptionLaw } = resolveTaxDetails(
            extra?.invoiceItemDetails,
            companyLegalInfo,
        );

        const rawTitle = extra?.invoiceItemDetails?.customTitle || e.name;
        const rawDescription = extra?.invoiceItemDetails?.customDescription || e.name;

        return {
            code: appointment ? applyAppointmentPlaceholders(rawTitle, appointment, services, client) : rawTitle,
            description: appointment ? applyAppointmentPlaceholders(rawDescription, appointment, services, client) : rawDescription,
            quantity: e.quantity,
            unitPrice: e.value,
            taxId,
            typeId: extra?.invoiceItemDetails?.type || InvoiceItemType.P,
            taxExemption,
            taxExemptionLaw,
        };
    });
};

/**
 * Merge invoice items with identical properties by summing quantities
 */
export const mergeInvoiceItems = (items: InvoiceItem[]): InvoiceItem[] => {
    const merged = new Map<string, InvoiceItem>();

    items.forEach((item) => {
        const key = [
            item.code,
            item.description,
            item.unitPrice,
            item.taxId,
            item.typeId,
            item.taxExemption,
            item.taxExemptionLaw,
            item.discountPercentage,
            item.unit,
        ].join('|');
        const existing = merged.get(key);
        if (existing) {
            merged.set(key, { ...existing, quantity: existing.quantity + item.quantity });
        } else {
            merged.set(key, { ...item });
        }
    });

    return Array.from(merged.values());
};

/**
 * Compare two arrays of invoice items for equality
 */
export const areInvoiceItemsEqual = (left: InvoiceItem[], right: InvoiceItem[]): boolean => {
    if (left.length !== right.length) return false;

    return left.every((item, index) => {
        const other = right[index];
        return item.code === other.code
            && item.description === other.description
            && item.unitPrice === other.unitPrice
            && item.quantity === other.quantity
            && item.taxId === other.taxId
            && item.typeId === other.typeId
            && item.taxExemption === other.taxExemption
            && item.taxExemptionLaw === other.taxExemptionLaw
            && item.discountPercentage === other.discountPercentage
            && item.unit === other.unit;
    });
};

export const DEFAULT_APPOINTMENT_PACK_DETAILS_TEMPLATE = '{date} - {appointment-type} - {professional} - {price}';

/**
 * Build appointment notes for invoice observations using a configurable template.
 * Supported placeholders: {date}, {appointment-type}, {invoice-appointment-type}, {professional}, {price}
 */
export const buildAppointmentNotes = (
    appointments: AppointmentData[],
    template: string = DEFAULT_APPOINTMENT_PACK_DETAILS_TEMPLATE,
    services?: AppointmentPlaceholderServices,
    client?: ClientData,
): string => {
    return appointments
        .slice()
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        .map(appointment => applyAppointmentPlaceholders(template, appointment, services, client))
        .join('\n');
};

/**
 * Sum all negative extras (expenses) from appointments with VAT applied
 * Discount must be calculated with VAT since globalDiscountAmount is applied AFTER VAT calculation
 */
export const sumNegativeExtras = (
    appointments: AppointmentData[],
    extrasSettings: any[],
    invoiceSettings: Pick<InvoiceSettingsData, 'companyLegalInfo' | 'branchesLegalInfo'> | undefined,
    taxRatesMap: { [key: string]: { rate: number } }
): number => {
    let totalDiscount = 0;

    appointments.forEach(appointment => {
        const negativeExpenses = (appointment.expenses ?? []).filter(e => e.value < 0);
        const companyLegalInfo = resolveEffectiveLegalInfo(invoiceSettings, appointment.branchId);

        negativeExpenses.forEach(expense => {
            // Get tax rate for this expense (same logic as mapExtrasToInvoiceItems)
            const extra = extrasSettings.find(ex => ex?.id === expense.id);
            const { taxId } = resolveTaxDetails(
                extra?.invoiceItemDetails,
                companyLegalInfo
            );
            const taxRate = (taxId ? taxRatesMap[taxId]?.rate : undefined) ?? 0;

            // Calculate discount base (absolute value of negative expense)
            const discountBase = Math.abs(expense.value) * expense.quantity;

            // Apply VAT to discount (since globalDiscountAmount is applied after VAT)
            const discountWithVAT = discountBase * (1 + taxRate / 100);

            totalDiscount += discountWithVAT;
        });
    });

    return totalDiscount;
};
